import { useEffect, useMemo } from 'react';
import { IntlShape } from 'react-intl';
import { RouteSuggestion } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/types';
import urls from '../../../../util/urls';
import { memberDetailMessages } from './messages';
import { MemberSection } from './types';

type UseMemberDetailOmnibarSuggestionsParams = {
  fountainLifeUXEnabled: boolean;
  fountainLifePowerUserEnabled: boolean;
  memberId?: string;
  memberDisplayName: string;
  section?: MemberSection;
  formatMessage: IntlShape['formatMessage'];
  searchValue: string;
  registerSuggestions: (options: {
    id: string;
    loading: boolean;
    suggestions: RouteSuggestion[];
    handler?: () => void;
  }) => void;
  unregisterSuggestions: (id: string) => void;
  setShowZoriChat: (value: boolean | ((prev: boolean) => boolean)) => void;
};

export const useMemberDetailOmnibarSuggestions = ({
  fountainLifeUXEnabled,
  fountainLifePowerUserEnabled,
  memberId,
  memberDisplayName,
  section,
  formatMessage,
  searchValue,
  registerSuggestions,
  unregisterSuggestions,
  setShowZoriChat,
}: UseMemberDetailOmnibarSuggestionsParams) => {
  const memberRouteSuggestions: RouteSuggestion[] = useMemo(() => {
    if (!fountainLifeUXEnabled || !memberId) {
      return [];
    }
    const sections = [
      {
        label: formatMessage(memberDetailMessages.onboardingSnapshotLabel),
        slug: 'onboarding-snapshot',
      },
      {
        label: formatMessage(memberDetailMessages.annualReportLabel),
        slug: 'annual-report',
      },
      {
        label: formatMessage(memberDetailMessages.actionPlanLabel),
        slug: 'action-plan',
      },
      {
        label: formatMessage(memberDetailMessages.aiToolsLabel),
        slug: 'ai-tools',
      },
      ...(fountainLifePowerUserEnabled
        ? [
            {
              label: formatMessage(memberDetailMessages.ruleActionAuditLabel),
              slug: 'rule-action-audit',
            },
          ]
        : []),
      { label: formatMessage(memberDetailMessages.labsLabel), slug: 'labs' },
      {
        label: formatMessage(memberDetailMessages.surveysLabel),
        slug: 'surveys',
      },
      {
        label: formatMessage(memberDetailMessages.documentsLabel),
        slug: 'documents',
      },
      {
        label: formatMessage(memberDetailMessages.memberAppLabel),
        slug: 'member-app',
      },
      {
        label: formatMessage(memberDetailMessages.omicsExplorerLabel),
        slug: 'omics-explorer',
      },
    ];

    return sections.map(({ label, slug }) => ({
      value: `${memberDisplayName} ${label}`.toLowerCase(),
      display: `${memberDisplayName} > ${label}`,
      path: `${urls.app.members(memberId)}/${slug}`,
      type: 'route' as const,
    }));
  }, [
    fountainLifePowerUserEnabled,
    fountainLifeUXEnabled,
    formatMessage,
    memberDisplayName,
    memberId,
  ]);

  useEffect(() => {
    if (!fountainLifeUXEnabled) {
      unregisterSuggestions('memberDetailRouteSuggestions');
      return;
    }
    const filteredRoutes = memberRouteSuggestions.filter(route =>
      route.value.includes(searchValue.toLowerCase()),
    );
    registerSuggestions({
      id: 'memberDetailRouteSuggestions',
      loading: false,
      suggestions: filteredRoutes,
    });
    return () => {
      unregisterSuggestions('memberDetailRouteSuggestions');
    };
  }, [
    fountainLifeUXEnabled,
    memberRouteSuggestions,
    registerSuggestions,
    searchValue,
    unregisterSuggestions,
  ]);

  useEffect(() => {
    if (!fountainLifeUXEnabled) {
      unregisterSuggestions('memberDetailZoriChat');
      return;
    }

    const query = searchValue.toLowerCase();
    const zoriSuggestion: RouteSuggestion = {
      value: 'zori chat',
      display: `${memberDisplayName} > Zori Chat`,
      path: `${urls.app.members(memberId)}/${section ?? 'annual-report'}`,
      type: 'route',
    };
    const suggestions = zoriSuggestion.value.includes(query)
      ? [zoriSuggestion]
      : [];

    registerSuggestions({
      id: 'memberDetailZoriChat',
      loading: false,
      suggestions,
      handler: () => {
        setShowZoriChat(true);
      },
    });

    return () => {
      unregisterSuggestions('memberDetailZoriChat');
    };
  }, [
    fountainLifeUXEnabled,
    memberDisplayName,
    memberId,
    registerSuggestions,
    searchValue,
    section,
    setShowZoriChat,
    unregisterSuggestions,
  ]);
};
